/**
 * App-level owner of the SOP Recorder session. The capture session, the
 * Document PiP popout, and the upload all live ABOVE the router — navigating
 * between HickoryOS pages must never end a recording, because recording
 * HickoryOS itself is the primary use case. The /sop-recorder page is just a
 * view of this state; the popout panel is portaled from here so it survives
 * any route change.
 *
 * Note: only context CONSUMERS re-render on the 250ms elapsed tick — the
 * ``children`` element identity is stable, so the app subtree bails out.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { RecorderPanel } from "@/components/sop-recorder/recorder-panel";
import { useBlobUrls } from "@/hooks/use-blob-urls";
import { useDocumentPip } from "@/hooks/use-document-pip";
import { useSopCapture, type SopCapturePayload } from "@/hooks/use-sop-capture";
import { downloadBlob } from "@/lib/csv";
import {
  isRetryableSaveError,
  newUploadState,
  saveRecording,
  zipRecording,
  type SaveRecordingResult,
  type UploadProgress,
  type UploadState,
} from "@/lib/sop-recording-upload";
import { SOP_RECORDINGS_KEY } from "@/hooks/use-sop-recordings";

export type { SaveRecordingResult } from "@/lib/sop-recording-upload";

export interface SavedSession {
  payload: SopCapturePayload;
  result: SaveRecordingResult;
}

interface SopRecorderContextValue {
  capture: ReturnType<typeof useSopCapture>;
  pipWindow: Window | null;
  /** getDisplayMedia + MediaRecorder + Document PiP all present. */
  supported: boolean;
  /** Object URLs for the live session's screenshots (order-matched). */
  liveUrls: string[];
  /** A captured session that hasn't saved yet — held until it saves, is
   *  downloaded-and-replaced by a new session, or the tab closes (which
   *  prompts first). A failed save NEVER drops it. */
  pendingPayload: SopCapturePayload | null;
  /** A live or not-yet-saved session exists only in this tab's memory — a
   *  reload would lose it. */
  unsaved: boolean;
  /** False after a deterministic rejection — resending can't succeed, but
   *  Download Recording still can. */
  canRetry: boolean;
  uploadProgress: UploadProgress | null;
  lastSaved: SavedSession | null;
  isSaving: boolean;
  handleStart: () => Promise<void>;
  handleStop: () => Promise<void>;
  handleCaptureScreenshot: () => Promise<void>;
  openPopout: () => Promise<void>;
  retryUpload: () => void;
  downloadRecording: () => Promise<void>;
}

const SopRecorderContext = createContext<SopRecorderContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- companion hook for the provider
export function useSopRecorder(): SopRecorderContextValue {
  const ctx = useContext(SopRecorderContext);
  if (!ctx) throw new Error("useSopRecorder must be used within SopRecorderProvider");
  return ctx;
}

export function SopRecorderProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  // The payload of a session whose upload hasn't succeeded yet — kept so a
  // failed save can be retried or downloaded without re-recording.
  const [pendingPayload, setPendingPayload] = useState<SopCapturePayload | null>(null);
  const [canRetry, setCanRetry] = useState(true);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  // Per-recording upload progress (minted URLs + files already PUT), so a
  // retry resumes instead of re-sending everything.
  const uploadStates = useRef(new WeakMap<SopCapturePayload, UploadState>());
  // The last successfully saved session, shown as a review until the next one.
  const [lastSaved, setLastSaved] = useState<SavedSession | null>(null);

  const save = useMutation({
    mutationFn: (payload: SopCapturePayload): Promise<SaveRecordingResult> => {
      let state = uploadStates.current.get(payload);
      if (!state) {
        state = newUploadState();
        uploadStates.current.set(payload, state);
      }
      return saveRecording(payload, state, setUploadProgress);
    },
    onSettled: () => setUploadProgress(null),
    onSuccess: (data, payload) => {
      setPendingPayload(null);
      uploadStates.current.delete(payload);
      setLastSaved({ payload, result: data });
      // Surface the fresh recording in the library even inside its staleTime.
      void queryClient.invalidateQueries({ queryKey: SOP_RECORDINGS_KEY, exact: true });
      toast.success(
        data.screenshot_count > 0
          ? `Recording saved — ${data.screenshot_count} screenshot${data.screenshot_count === 1 ? "" : "s"}.`
          : "Recording saved.",
      );
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Upload failed";
      // Never drop the payload: even a deterministic rejection (resending
      // can't succeed) leaves the user a local copy via Download Recording.
      const retryable = isRetryableSaveError(err);
      setCanRetry(retryable);
      toast.error(
        retryable
          ? `${msg} — your recording is still here. Retry Upload, or Download Recording to keep a copy.`
          : `${msg} — use Download Recording to keep a copy.`,
        { duration: Infinity },
      );
    },
  });

  const startSave = useCallback(
    (payload: SopCapturePayload) => {
      setPendingPayload(payload);
      setCanRetry(true);
      save.mutate(payload);
    },
    [save],
  );

  const pip = useDocumentPip({ width: 340, height: 600, title: "SOP Recorder" });
  const capture = useSopCapture({
    onExternalStop: (payload) => {
      // "Stop sharing" from Chrome's bar ended the session.
      pip.close();
      if (payload) {
        toast("Screen sharing ended — saving the recording.");
        startSave(payload);
      } else {
        toast("Screen sharing ended — nothing was recorded.");
      }
    },
  });

  const liveUrls = useBlobUrls(capture.screenshots.map((s) => s.blob));

  const openPopout = useCallback(async () => {
    const opened = await pip.open();
    if (!opened) {
      toast("Chrome blocked the popout — click Open Popout to float the recorder.");
    }
  }, [pip]);

  const handleStart = useCallback(async () => {
    if (pendingPayload) {
      // One retry slot: a new session replaces the unsaved one — say so
      // instead of silently clobbering it.
      setPendingPayload(null);
      toast("Starting a new session — the unsaved recording was discarded.");
    }
    setLastSaved(null);
    const result = await capture.start();
    if (!result.ok) {
      if (result.cancelled) {
        toast("Screen share cancelled — no session started.");
      } else if (result.error) {
        toast.error(result.error);
      }
      return;
    }
    await openPopout();
  }, [capture, openPopout, pendingPayload]);

  const handleCaptureScreenshot = useCallback(async () => {
    const ok = await capture.captureScreenshot();
    if (!ok) toast("Couldn't grab a frame — try again in a second.");
  }, [capture]);

  const handleStop = useCallback(async () => {
    const payload = await capture.stop();
    pip.close();
    if (!payload) {
      toast("Nothing was recorded.");
      return;
    }
    startSave(payload);
  }, [capture, pip, startSave]);

  const retryUpload = useCallback(() => {
    if (pendingPayload) save.mutate(pendingPayload);
  }, [pendingPayload, save]);

  const downloadRecording = useCallback(async () => {
    if (!pendingPayload) return;
    const { filename, blob } = await zipRecording(pendingPayload);
    downloadBlob(filename, blob);
  }, [pendingPayload]);

  // A live session — or a captured one that hasn't saved — must survive an
  // accidental tab close/reload attempt: it lives only in this tab's memory,
  // so make the browser ask first.
  const unsaved = capture.status === "recording" || pendingPayload !== null;
  useEffect(() => {
    if (!unsaved) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [unsaved]);

  const value: SopRecorderContextValue = {
    capture,
    pipWindow: pip.pipWindow,
    supported: capture.isSupported && pip.isSupported,
    liveUrls,
    pendingPayload,
    unsaved,
    canRetry,
    uploadProgress,
    lastSaved,
    isSaving: save.isPending,
    handleStart,
    handleStop,
    handleCaptureScreenshot,
    openPopout,
    retryUpload,
    downloadRecording,
  };

  return (
    <SopRecorderContext.Provider value={value}>
      {children}
      {pip.pipWindow &&
        createPortal(
          <RecorderPanel
            elapsed={capture.elapsed}
            shots={capture.screenshots}
            urls={liveUrls}
            micMuted={capture.micMuted}
            onMicMutedChange={capture.setMicMuted}
            autoCapture={capture.autoCapture}
            onAutoCaptureChange={capture.setAutoCapture}
            onMove={capture.moveScreenshot}
            onRemove={capture.removeScreenshot}
            onCapture={() => void handleCaptureScreenshot()}
            onStop={() => void handleStop()}
          />,
          pip.pipWindow.document.body,
        )}
    </SopRecorderContext.Provider>
  );
}
